/**
 * ConfirmDialog Component
 *
 * Reusable confirmation dialog to replace browser window.confirm() calls.
 * Uses the existing Modal component with consistent styling.
 *
 * Usage:
 *   const { confirm } = useConfirm();
 *   const ok = await confirm('Disconnect this account?', { variant: 'warning' });
 *   if (ok) { ... }
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import Modal from './Modal';
import { Button } from './Button';
import { AlertTriangle, Trash2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

type ConfirmVariant = 'danger' | 'warning' | 'info';

interface ConfirmOptions {
  title?: string;
  variant?: ConfirmVariant;
  confirmLabel?: string;
  cancelLabel?: string;
}

interface ConfirmState {
  message: string;
  options: ConfirmOptions;
  resolve: (value: boolean) => void;
}

interface ConfirmContextType {
  confirm: (message: string, options?: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextType | null>(null);

export const useConfirm = (): ConfirmContextType => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx;
};

const variantConfig: Record<ConfirmVariant, {
  icon: React.ReactNode;
  iconBg: string;
  buttonVariant: 'error' | 'primary';
  defaultTitle: string;
}> = {
  danger: {
    icon: <Trash2 className="w-6 h-6 text-bad" />,
    iconBg: 'bg-bad-soft',
    buttonVariant: 'error',
    defaultTitle: 'Are you sure?',
  },
  warning: {
    icon: <AlertTriangle className="w-6 h-6 text-warn" />,
    iconBg: 'bg-warn-soft',
    buttonVariant: 'primary',
    defaultTitle: 'Confirm action',
  },
  info: {
    icon: <AlertCircle className="w-6 h-6 text-primary" />,
    iconBg: 'bg-work-soft',
    buttonVariant: 'primary',
    defaultTitle: 'Confirm',
  },
};

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<ConfirmState | null>(null);

  const confirm = useCallback((message: string, options: ConfirmOptions = {}): Promise<boolean> => {
    return new Promise(resolve => {
      setState({ message, options, resolve });
    });
  }, []);

  const handleClose = useCallback((value: boolean) => {
    state?.resolve(value);
    setState(null);
  }, [state]);

  const variant = state?.options.variant || 'danger';
  const config = variantConfig[variant];

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {state && (
        <Modal
          isOpen={true}
          onClose={() => handleClose(false)}
          size="sm"
        >
          <Modal.Body>
            <div className="flex flex-col items-center text-center">
              <div className={cn('w-12 h-12 rounded-full flex items-center justify-center mb-4', config.iconBg)}>
                {config.icon}
              </div>
              <h3 className="text-lg font-semibold text-ink mb-2">
                {state.options.title || config.defaultTitle}
              </h3>
              <p className="text-sm text-ink-2">
                {state.message}
              </p>
            </div>
          </Modal.Body>
          <Modal.Footer align="right">
            <Button variant="outline" onClick={() => handleClose(false)}>
              {state.options.cancelLabel || 'Cancel'}
            </Button>
            <Button variant={config.buttonVariant} onClick={() => handleClose(true)}>
              {state.options.confirmLabel || (variant === 'danger' ? 'Delete' : 'Confirm')}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </ConfirmContext.Provider>
  );
};

export default ConfirmProvider;
